import { Box, Paper, Typography } from "@mui/material";
import type { InspectionDashboardTotals } from "../../../models/InspectionDashboard";
import {
	formatCount,
	formatPercent,
	formatRatio,
	rate,
} from "../../utils/dashboardFormat";
import { DASHBOARD_INK } from "../../utils/dashboardTokens";

type Tile = {
	label: string;
	value: string;
	caption: string;
};

const share = (part: number, whole: number) =>
	formatPercent(rate(part, whole) ?? 0);

const buildTiles = (totals: InspectionDashboardTotals): Tile[] => [
	{
		label: "Sheets",
		value: formatCount(totals.Sheets),
		caption: `${formatCount(totals.SheetsWithFinding)} with a finding`,
	},
	{
		label: "Readings",
		value: formatCount(totals.Readings),
		caption: `${totals.Sheets > 0 ? formatRatio(totals.Readings, totals.Sheets, 0) : "0"} per sheet`,
	},
	{
		label: "With a no-go",
		value: share(totals.ReadingsWithNoGo, totals.Readings),
		caption: `${formatCount(totals.ReadingsWithNoGo)} readings`,
	},
	{
		label: "With a defect",
		value: share(totals.ReadingsWithDefect, totals.Readings),
		caption: `${formatCount(totals.ReadingsWithDefect)} readings`,
	},
];

export const DashboardTiles = ({
	totals,
}: {
	totals: InspectionDashboardTotals;
}) => (
	<Paper
		elevation={0}
		sx={{
			border: 1,
			borderColor: "divider",
			borderRadius: 1,
			display: "grid",
			gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
		}}
	>
		{buildTiles(totals).map((tile, index) => (
			<Box
				key={tile.label}
				sx={{
					px: 2,
					py: 1.5,
					borderLeft: index === 0 ? 0 : 1,
					borderColor: "divider",
				}}
			>
				<Typography
					sx={{
						color: DASHBOARD_INK.SECONDARY,
						fontSize: "0.8125rem",
						fontWeight: 500,
					}}
				>
					{tile.label}
				</Typography>
				<Typography
					sx={{
						color: DASHBOARD_INK.PRIMARY,
						fontSize: "1.7rem",
						fontWeight: 600,
						lineHeight: 1.2,
					}}
				>
					{tile.value}
				</Typography>
				<Typography sx={{ color: DASHBOARD_INK.MUTED, fontSize: "0.8125rem" }}>
					{tile.caption}
				</Typography>
			</Box>
		))}
	</Paper>
);
